import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fieldValue, usePolarisEvents } from '../../app/components/polaris';
import { formatSharePercent, getSharePercent } from './reportShare';

type VehicleReportTarget = {
    vehicleLabel: string;
    fieldValues: Record<string, string>;
};

export const getSearchLabel = (vehicle: VehicleReportTarget) => vehicle.vehicleLabel?.trim()
    || Object.values(vehicle.fieldValues ?? {}).filter(Boolean).join(' ')
    || 'Unknown vehicle';

const normalizedFields = (vehicle: VehicleReportTarget) => Object.entries(vehicle.fieldValues ?? {})
    .reduce<Record<string, string>>((fields, [key, value]) => {
        if (key.trim() && value?.trim()) fields[key.trim().toLowerCase()] = value.trim();
        return fields;
    }, {});

const fieldLabel = (key: string) => key.replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase()).replace(/\bCc\b/g, 'CC');

const VehicleIdentity = ({ vehicle, rank }: { vehicle: VehicleReportTarget; rank: number }) => {
    const navigate = useNavigate();
    const fields = normalizedFields(vehicle);
    const label = getSearchLabel(vehicle);
    const titleFields = ['make', 'model', 'submodel'];
    const hasIdentity = Boolean(fields.make || fields.model);
    const title = hasIdentity ? titleFields.map(key => fields[key]).filter(Boolean).join(' ') : label;
    const details = Object.entries(fields)
        .filter(([key]) => !hasIdentity || !titleFields.includes(key))
        .sort(([left], [right]) => Number(right === 'year') - Number(left === 'year') || left.localeCompare(right))
        .map(([key, value]) => key === 'year' ? value : `${fieldLabel(key)}: ${value}`);
    const params = new URLSearchParams();
    params.set('fieldValues', JSON.stringify(fields));
    ['year', ...titleFields].forEach(key => { if (fields[key]) params.set(key, fields[key]); });

    return (
        <s-grid gridTemplateColumns="24px 1fr" gap="small-200">
            <s-text color="subdued">{rank}</s-text>
            <s-stack gap="none">
                {Object.keys(fields).length ? (
                    <s-link accessibilityLabel={`View products for ${label}`}
                        onClick={() => navigate(`/products?${params.toString()}`)}>
                        {title}
                    </s-link>
                ) : <s-text fontWeight="semibold">{title}</s-text>}
                {details.length > 0 && <s-text color="subdued">{details.join(' · ')}</s-text>}
            </s-stack>
        </s-grid>
    );
};

export type VehicleReportRow = VehicleReportTarget & {
    currentMappedProductCount: number;
    searchCount?: number;
    previousSearchCount?: number;
    lastSearchedOn?: string;
};

type DetailMode = 'searches' | 'opportunities' | 'health';
type Props = {
    title: string;
    mode: DetailMode;
    rows: VehicleReportRow[];
    totalSearches?: number;
    comparison?: boolean;
    comparisonComplete?: boolean;
    notice?: ReactNode;
    controls?: ReactNode;
    emptyMessage: ReactNode;
};

const count = (value = 0) => value.toLocaleString();
const lastSearched = (value?: string) => {
    const date = new Date(`${value?.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
    });
};

export const pageSizeOptions = ['10', '25', '50'];

export default function VehicleReportDetails({ title, mode, rows, comparison = false,
    comparisonComplete = true, totalSearches, notice, controls, emptyMessage }: Props) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const coverage = mode === 'health' || mode === 'opportunities';
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * pageSize;
    const tableEvents = usePolarisEvents({
        nextpage: () => setPage(Math.min(pageCount, safePage + 1)),
        previouspage: () => setPage(Math.max(1, safePage - 1)),
    });
    const columns: { label: string; numeric?: boolean; value: (row: VehicleReportRow) => ReactNode }[] = [];

    // Numeric cells hold plain text so the table aligns them under their right-aligned headers.
    if (mode !== 'health') {
        columns.push({ label: 'Searches', numeric: true, value: row => count(row.searchCount) });
        columns.push({ label: 'Share', numeric: true,
            value: row => formatSharePercent(getSharePercent(row.searchCount ?? 0, totalSearches ?? 0)) });
    }
    if (coverage) {
        columns.push({ label: 'Mapped products', numeric: true, value: row => count(row.currentMappedProductCount) });
        columns.push({ label: 'Coverage', value: row => row.currentMappedProductCount === 0 ? 'No mappings' : 'Low coverage' });
    } else if (comparison) {
        columns.push({ label: comparisonComplete ? 'Comparison' : 'Comparison (partial)', numeric: true,
            value: row => count(row.previousSearchCount) });
        columns.push({ label: 'Change', numeric: true, value: row => {
            if (!comparisonComplete) return '—';
            // Vehicles with no searches in the comparison period are new rather than a raw increase.
            if (row.previousSearchCount === 0 && (row.searchCount ?? 0) > 0) return 'New';
            const change = (row.searchCount ?? 0) - (row.previousSearchCount ?? 0);
            return `${change > 0 ? '+' : ''}${count(change)}`;
        } });
    }
    if (mode !== 'health') columns.push({ label: 'Last searched', value: row => lastSearched(row.lastSearchedOn) });

    return (
        <s-stack gap="base">
            <s-stack direction="inline" gap="small-200" alignItems="baseline">
                <s-heading>{title}</s-heading>
                <s-text color="subdued">{`${count(rows.length)} ${rows.length === 1 ? 'vehicle' : 'vehicles'}`}</s-text>
            </s-stack>
            {controls}
            {notice}
            {rows.length === 0 ? <s-text color="subdued">{emptyMessage}</s-text> : (
                <>
                    <s-table ref={tableEvents} paginate={pageCount > 1}
                        hasPreviousPage={safePage > 1} hasNextPage={safePage < pageCount}>
                        <s-table-header-row>
                            <s-table-header listSlot="primary">Vehicle</s-table-header>
                            {columns.map(column => (
                                <s-table-header key={column.label} listSlot="labeled"
                                    format={column.numeric ? 'numeric' : 'base'}>
                                    {column.label}
                                </s-table-header>
                            ))}
                        </s-table-header-row>
                        <s-table-body>
                            {rows.slice(start, start + pageSize).map((row, index) => (
                                <s-table-row key={JSON.stringify([row.fieldValues, row.vehicleLabel, start + index])}>
                                    <s-table-cell><VehicleIdentity vehicle={row} rank={start + index + 1} /></s-table-cell>
                                    {columns.map(column => <s-table-cell key={column.label}>{column.value(row)}</s-table-cell>)}
                                </s-table-row>
                            ))}
                        </s-table-body>
                    </s-table>
                    <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                        <s-text color="subdued">
                            {`${count(start + 1)}–${count(Math.min(start + pageSize, rows.length))} of ${count(rows.length)} vehicles`}
                        </s-text>
                        <s-select label="Rows per page" labelAccessibilityVisibility="exclusive" value={String(pageSize)}
                            onChange={event => { setPageSize(Number(fieldValue(event))); setPage(1); }}>
                            {pageSizeOptions.map(value => <s-option key={value} value={value}>{`${value} per page`}</s-option>)}
                        </s-select>
                    </s-grid>
                </>
            )}
        </s-stack>
    );
}
